import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { Component, Inject, OnDestroy } from '@angular/core';
import { DayPart, DayPartsDialogData } from '@interfaces/dayParts/1.0';
import { DecoratorData } from '@components/item-decorator';
import { Language } from '@interfaces/language/1.0';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MessageType } from '@constants/message/1.0';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { BrandService } from '@services/brand/1.0/brand.service';
import { DecoratorService } from '@services/decorator/1.0/decorator.service';
import { Decorator, DecoratorType } from '@interfaces/decorator/1.0';
import { LoadingColor } from '@constants/loading/1.0';
import { TceLocaleService } from '@services/tce-locale/1.0/tce-locale.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { StoreService } from '@services/store/1.0/store.service';
import { IsEqualService } from '@services/utilities/1.0/is-equal.service';
import { TenantSchedule, WeekHours } from '@generated/tcea/models';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';
import { MessageComponent } from '../../message/1.0/message.component';
import { LoadingComponent } from '../../loading/1.0/loading.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ItemDecoratorCvaComponent } from '../../item-decorator/item-decorator-cva.component';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { StoreHoursTemplateFormComponent } from '../../store-hours-template-form/1.0/store-hours-template-form.component';
import { ButtonComponent } from '../../button/1.0/button.component';

@Component({
  selector: 'txp-create-daypart-dialog',
  templateUrl: './create-daypart-dialog.component.html',
  styleUrls: ['./create-daypart-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    MessageComponent,
    LoadingComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ItemDecoratorCvaComponent,
    MatSelect,
    NgFor,
    MatOption,
    StoreHoursTemplateFormComponent,
    MatDialogActions,
    ButtonComponent,
    TranslateModule,
  ],
})
export class CreateDayPartsDialogComponent implements OnDestroy {
  noWhitespaceValidator = (control: AbstractControl): ValidationErrors | null => {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  };
  form: UntypedFormGroup;
  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  messageType = MessageType;
  loadingColor = LoadingColor;
  showFormError: boolean;
  showError: boolean;
  isSaving: boolean;
  brandId: string;
  onChangeFn: any;
  decoratorData: DecoratorData;
  unsubscribe = new Subject<void>();
  languages: Language[];
  showDataLoadError = false;
  title: string;
  dayPartsList: DayPart[];
  savedId: string;
  readOnly = false;
  isLoading = false;
  isStoreLevel = false;
  isMenuLevel = false;
  availableBrandDayparts: Array<{ id: string; name: string }> = [];
  selectedDaypartDecorators: Decorator[] = [];
  templateList: TenantSchedule[] = [];
  template: Partial<TenantSchedule>;
  errorMessage: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<CreateDayPartsDialogComponent>,
    private translateService: TranslateService,
    private tceLocaleService: TceLocaleService,
    @Inject(MAT_DIALOG_DATA) public data: DayPartsDialogData,
    private permissionsService: PermissionsService,
    private brandService: BrandService,
    private decoratorService: DecoratorService,
    private storeService: StoreService,
  ) {
    this.brandId = sessionStorage.getItem('brand_id');
    this.isStoreLevel = data.isStoreLevel || false;
    this.isMenuLevel = data.isMenuLevel || false;
    this.loadLocaleData();

    if (!this.isStoreLevel && !this.isMenuLevel) {
      this.decoratorData = {
        keys: [],
        fields: [
          {
            property: 'name',
            label: this.translateService.instant('menu.information.dayParts.name'),
          },
        ],
      };
      this.decoratorData.keys = this.getDecoratorKeys(data?.supportedLanguages || []);
    }

    this.readOnly = !this.permissionsService.getPermission(
      this.isStoreLevel || this.isMenuLevel
        ? this.isStoreLevel
          ? 'store:write'
          : 'menu:write'
        : 'tenant:write',
    );
    this.title = data.title;
    this.savedId = data?.dayPart?.id;
    this.dayPartsList = data.dayPartsList;

    if (this.isStoreLevel || this.isMenuLevel) {
      // Store-level or Menu-level form
      this.form = this.formBuilder.group({
        daypartId: new UntypedFormControl('', [Validators.required]),
        decorators: new UntypedFormControl({ value: [], disabled: true }), // Read-only decorators from brand daypart
        hoursTemplate: new UntypedFormControl(''),
        hours: new UntypedFormControl({}, [
          Validators.required,
          (control: AbstractControl) => {
            const value = control.value;
            const isEmpty =
              !value || (typeof value === 'object' && Object.keys(value).length === 0);
            return isEmpty ? { required: true } : null;
          },
        ]),
      });

      // Setup decorator data for display
      this.decoratorData = {
        keys: [],
        fields: [
          {
            property: 'name',
            label: this.translateService.instant('menu.information.dayParts.name'),
          },
        ],
      };
      this.decoratorData.keys = this.getDecoratorKeys(data?.supportedLanguages || []);

      // Setup available brand dayparts for selection
      this.setupAvailableBrandDayparts();

      // Populate template list from brand schedule
      this.populateTemplateList(data.brand);

      if (data.dayPart) {
        this.form.patchValue({
          daypartId: data.dayPart.id,
          hours: data.dayPart.hours, // Load existing hours from store/menu daypart
        });
        this.form.get('daypartId').disable(); // Disable selection in edit mode
        this.onDaypartSelectionChange(data.dayPart.id);
        // Set template based on hours (if it matches)
        this.form
          .get('hoursTemplate')
          .setValue(this.findTemplate(this.form.get('hours').value), { emitEvent: false });
      }

      // Listen to daypart selection changes
      this.form
        .get('daypartId')
        ?.valueChanges.pipe(takeUntil(this.unsubscribe))
        .subscribe((daypartId) => {
          if (daypartId) {
            this.onDaypartSelectionChange(daypartId);
          }
        });
    } else {
      // Brand-level form
      this.form = this.formBuilder.group({
        name: new UntypedFormControl('', [
          Validators.required,
          this.noWhitespaceValidator,
          this.nameExistsValidator.bind(this),
        ]),
        decorators: new UntypedFormControl([]),
      });
      if (data.dayPart) {
        this.form.patchValue(data.dayPart);
      }
      if (data.decorators) {
        this.form.get('decorators').setValue(data.decorators);
      }
    }

    this.readOnly ? this.form.disable() : this.form.enable();
  }

  loadLocaleData() {
    this.tceLocaleService
      .getLocaleData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (localeData) => {
          this.languages = localeData.languages;
        },
        error: () => {
          this.showDataLoadError = true;
        },
      });
  }

  getDecoratorKeys(sl: string[]): DecoratorData['keys'] {
    if (!sl || !this.languages) {
      return [];
    }
    const keys: DecoratorData['keys'] = [];
    for (let i = 0, iLen = sl.length; i < iLen; i++) {
      const lang = this.languages.filter((item) => item.languageCode === sl[i]);
      if (lang.length > 0) {
        keys.push({ property: lang[0].languageCode, label: lang[0].languageName });
      }
    }
    return keys;
  }

  setupAvailableBrandDayparts() {
    if (!this.data.brandDayparts) {
      this.availableBrandDayparts = [];
      return;
    }

    const brandDaypartIds = Object.keys(this.data.brandDayparts);
    const configuredDaypartIds = Object.keys(
      (this.isStoreLevel ? this.data.storeDayparts : this.data.menuDayparts) || {},
    );
    const isEditMode = !!this.data.dayPart;

    // Filter logic:
    // - In edit mode: only show the current daypart
    // - In create mode: show brand dayparts that are NOT yet configured at store/menu level
    const filteredIds = isEditMode
      ? brandDaypartIds.filter((id) => id === this.data.dayPart.id)
      : brandDaypartIds.filter((id) => !configuredDaypartIds.includes(id));

    this.availableBrandDayparts = filteredIds.map((id) => ({
      id,
      name: this.data.brandDayparts[id].name,
    }));

    this.availableBrandDayparts = this.availableBrandDayparts.sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  compareDayparts(o1: any, o2: any): boolean {
    return o1 === o2;
  }

  onDaypartSelectionChange(daypartId: string) {
    if (!daypartId || !this.data.brandDayparts) {
      this.selectedDaypartDecorators = [];
      if (this.isStoreLevel || this.isMenuLevel) {
        this.form.patchValue({ decorators: [] });
      }
      return;
    }

    const selectedDaypart = this.data.brandDayparts[daypartId];

    if (selectedDaypart && (this.isStoreLevel || this.isMenuLevel)) {
      // Load decorators from the decorator service for the selected brand daypart
      this.isLoading = true;
      this.decoratorService
        .get(this.brandId, daypartId)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (decorators: Decorator[]) => {
            // Filter decorators for this specific daypart
            let daypartDecorators = (decorators || []).filter(
              (decorator) => decorator.entityId === daypartId,
            );

            // If no decorators exist, initialize with empty ones for each language
            if (daypartDecorators.length === 0 && this.decoratorData?.keys) {
              daypartDecorators = this.decoratorData.keys.map((key) => ({
                key: key.property,
                field: 'name',
                value: undefined,
                entityId: daypartId,
              }));
            }

            this.selectedDaypartDecorators = daypartDecorators;
            this.form.patchValue({ decorators: daypartDecorators });
            this.isLoading = false;
          },
          error: (err) => {
            // If loading fails, initialize with empty decorators for each language
            const emptyDecorators = this.decoratorData.keys.map((key) => ({
              key: key.property,
              field: 'name',
              value: undefined,
              entityId: daypartId,
            }));
            this.selectedDaypartDecorators = emptyDecorators;
            this.form.patchValue({ decorators: emptyDecorators });
            this.isLoading = false;
          },
        });
    } else {
      this.selectedDaypartDecorators = [];
      if (this.isStoreLevel) {
        this.form.patchValue({ decorators: [] });
      }
    }
  }

  getSelectedDaypartName(): string {
    const daypartId = this.form.get('daypartId')?.value;
    if (!daypartId || !this.data.brandDayparts) {
      return '';
    }
    return this.data.brandDayparts[daypartId]?.name || '';
  }

  populateTemplateList(brand: any) {
    this.templateList = brand?.schedule || [];
  }

  /**
   * Returns the template item in templateList that matches the given hours or undefined if not found
   *
   * @param hours - hours to find
   */
  findTemplate(hours: WeekHours): TenantSchedule {
    if (hours && this.templateList) {
      for (const template of this.templateList) {
        if (IsEqualService.isEqual(template.hours, hours)) {
          return template;
        }
      }
    }
    return undefined;
  }

  /**
   * Normalizes time format to ensure HH:MM format with leading zeros
   * Converts "0:55" to "00:55", "9:30" to "09:30", etc.
   */
  private normalizeTimeFormat(hours: WeekHours): WeekHours {
    if (!hours) return hours;

    const normalized = {};
    Object.keys(hours).forEach((day) => {
      if (Array.isArray(hours[day])) {
        normalized[day] = hours[day].map((slot) => ({
          startTime: this.formatTime(slot.startTime),
          endTime: this.formatTime(slot.endTime),
        }));
      }
    });
    return normalized;
  }

  /**
   * Formats time string to HH:MM format with leading zeros
   * Example: "0:55" -> "00:55", "9:30" -> "09:30"
   */
  private formatTime(time: string): string {
    if (!time) return time;

    const parts = time.split(':');
    if (parts.length !== 2) return time;

    const hours = parts[0].padStart(2, '0');
    const minutes = parts[1].padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  onTemplateChange(template: TenantSchedule) {
    if (template && template.hours) {
      // Clone the hours and ensure it's a pure object without any extra properties
      const clonedHours = JSON.parse(JSON.stringify(template.hours));
      // Normalize time format to ensure consistent HH:MM format
      const normalizedHours = this.normalizeTimeFormat(clonedHours);
      this.form.get('hours').setValue(normalizedHours);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (this.onChangeFn) {
        this.onChangeFn(value);
      }
    });
  }

  onCancel() {
    this.dialogRef.close();
  }

  saveDaypart() {
    // For store or menu level, check hours field
    if (this.isStoreLevel || this.isMenuLevel) {
      const hours = this.form.get('hours')?.value;
      const hasHours = hours && Object.keys(hours).length > 0;

      if (!this.form.valid || !hasHours) {
        this.showFormError = true;
        return;
      }
    } else {
      // For brand level, only check form validity
      if (!this.form.valid) {
        this.showFormError = true;
        return;
      }
    }

    this.showFormError = false;
    this.showError = false;
    this.isSaving = true;

    if (this.isStoreLevel) {
      this.handleStoreLevelSave();
    } else if (this.isMenuLevel) {
      this.handleMenuLevelSave();
    } else if (this.savedId) {
      this.handleUpdate();
    } else {
      this.handleCreate();
    }
  }

  handleStoreLevelSave() {
    const daypartId = this.savedId || this.form.get('daypartId').value;
    const hours = this.form.get('hours').value;
    const brandDaypart = this.data.brandDayparts[daypartId];

    // Ensure hours is a clean object without any extra properties
    const cleanHours = JSON.parse(JSON.stringify(hours));
    // Normalize time format to ensure HH:MM format with leading zeros (e.g., "8:00" -> "08:00")
    const normalizedHours = this.normalizeTimeFormat(cleanHours);

    this.storeService
      .saveStoreDaypart(this.data.brand.id, this.data.store.id, daypartId, normalizedHours)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.isSaving = false;
          const dayPart: DayPart = {
            id: daypartId,
            name: brandDaypart.name,
            hours: normalizedHours,
          };
          this.dialogRef.close(dayPart);
        },
        error: (err) => {
          this.isSaving = false;
          this.showError = true;
          // Extract error message if available
          if (err?.error?.message) {
            this.errorMessage = err.error.message;
          } else if (err?.message) {
            this.errorMessage = err.message;
          }
        },
      });
  }

  handleMenuLevelSave() {
    const daypartId = this.savedId || this.form.get('daypartId').value;
    const hours = this.form.get('hours').value;
    const brandDaypart = this.data.brandDayparts[daypartId];

    // Ensure hours is a clean object without any extra properties
    const cleanHours = JSON.parse(JSON.stringify(hours));
    // Normalize time format to ensure HH:MM format with leading zeros (e.g., "8:00" -> "08:00")
    const normalizedHours = this.normalizeTimeFormat(cleanHours);

    this.storeService
      .saveMenuDaypart(this.data.brand.id, this.data.menu.menuId, daypartId, normalizedHours)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          this.isSaving = false;
          const dayPart: DayPart = {
            id: daypartId,
            name: brandDaypart?.name || '',
            hours: normalizedHours,
          };
          this.dialogRef.close(dayPart);
        },
        error: (err) => {
          this.isSaving = false;
          this.showError = true;
          // Extract error message if available
          if (err?.error?.message) {
            this.errorMessage = err.error.message;
          } else if (err?.message) {
            this.errorMessage = err.message;
          }
        },
      });
  }

  handleCreate() {
    const daypartData = {
      name: this.form.get('name').value.trim(),
    };

    this.brandService
      .createDaypart(this.brandId, daypartData)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          const daypartId = response?.daypartId;
          if (daypartId) {
            // Save decorators after creating daypart
            this.saveDecorators(daypartId).subscribe({
              next: (decorators) => {
                const dayPart: DayPart = {
                  id: daypartId,
                  name: daypartData.name,
                  decorators: decorators,
                };
                this.handleSaveComplete(dayPart);
              },
              error: (error) => {
                this.handleSaveError();
              },
            });
          } else {
            this.handleSaveError();
          }
        },
        error: (error) => {
          this.handleSaveError();
        },
      });
  }

  handleUpdate() {
    const daypartData = {
      name: this.form.get('name').value.trim(),
    };

    // Update daypart and decorators in parallel
    forkJoin([
      this.brandService.updateDaypart(this.brandId, this.savedId, daypartData),
      this.saveDecorators(this.savedId),
    ])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: ([_, decorators]) => {
          const dayPart: DayPart = {
            id: this.savedId,
            name: daypartData.name,
            decorators: decorators,
          };
          this.handleSaveComplete(dayPart);
        },
        error: this.handleSaveError,
      });
  }

  saveDecorators(daypartId: string): Observable<Decorator[]> {
    const decorators = this.form.get('decorators').value;

    if (decorators && decorators.length > 0) {
      const decoratorsToSave = decorators.map((decorator) => ({
        ...decorator,
        entityId: daypartId,
      }));
      return this.decoratorService
        .save(this.brandId, daypartId, decoratorsToSave, DecoratorType.MENU)
        .pipe(
          catchError((e) => {
            return of([]);
          }),
        );
    }
    return of([]);
  }

  handleSaveComplete(dayPart: DayPart) {
    this.isSaving = false;
    this.dialogRef.close(dayPart);
  }

  handleSaveError = () => {
    this.showError = true;
    this.isSaving = false;
  };

  nameExistsValidator(control: AbstractControl): ValidationErrors | undefined {
    if (!this.dayPartsList?.length) return undefined;
    const existingNames = this.dayPartsList
      .filter((dayPart) => dayPart.id !== this.savedId)
      .map((dayPart) => dayPart.id);
    const nameExists = existingNames.includes(control.value);
    return nameExists ? { nameExists: true } : undefined;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
